'use client';

import { useEffect, useState } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { BehaviorSubject } from 'rxjs';

import { fetchPinnedMessages } from '../functions/message/fetchPinnedMessages';
import type { ExtendedCommunityChannelMessage } from '../types/ExtendedCommunityChannelMessage';

interface State {
  messages: ExtendedCommunityChannelMessage[];
  isLoading: boolean;
  error: Error | undefined;
}

const kInitial: State = {
  messages: [],
  isLoading: true,
  error: undefined,
};

/**
 * Pinned-only list for a channel. Seeded via SearchApi with the
 * `pinnedOnly` filter. Slice 3 doesn't add a dedicated Firestore
 * sub for pinned messages — the channel's main message
 * subscription already covers `pinnedAt` flips on top-level
 * messages, so when a user pins/un-pins from the bubble kebab the
 * pinned panel needs a one-shot refetch instead of relying on the
 * existing live stream (since pinned uses a different filter +
 * sort).
 *
 * `refresh` re-fetches; callers wire it up to the pin-toggled
 * event (the kebab's onClick after a successful toggle).
 */
export function usePinnedMessages(
  creatorId: string | null | undefined,
  channelId: string | null | undefined,
) {
  const [state$] = useState(() => new BehaviorSubject<State>(kInitial));

  useEffect(() => {
    if (!creatorId || !channelId) {
      state$.next(kInitial);
      return;
    }
    let cancelled = false;
    state$.next({ messages: [], isLoading: true, error: undefined });
    void fetchPinnedMessages(creatorId, channelId)
      .then(messages => {
        if (cancelled) return;
        state$.next({ messages, isLoading: false, error: undefined });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        state$.next({
          messages: [],
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId, channelId, state$]);

  return state$;
}

export function usePinnedMessagesSnapshot(
  creatorId: string | null | undefined,
  channelId: string | null | undefined,
): State {
  const state$ = usePinnedMessages(creatorId, channelId);
  const [state] = useBehaviorSubject(state$);
  return state;
}

/**
 * Refresh action — re-fetches the pinned list using the same
 * function used for the seed. Used by the kebab's pin/un-pin
 * handler so the panel reflects the change without waiting for a
 * separate Firestore listener.
 */
export function useRefreshPinnedMessages(
  state$: BehaviorSubject<State>,
  creatorId: string | null | undefined,
  channelId: string | null | undefined,
) {
  return async () => {
    if (!creatorId || !channelId) return;
    try {
      const messages = await fetchPinnedMessages(creatorId, channelId);
      state$.next({ messages, isLoading: false, error: undefined });
    } catch (error: unknown) {
      state$.next({
        ...state$.getValue(),
        isLoading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  };
}
